import type { ILink } from "@components/core/Link";
import type { IImage } from "@components/types/media";
import type { VariantProps } from "class-variance-authority";

import { Image } from "@components/core/Image";
import { Link } from "@components/core/Link";
import { isValidImage } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { breakpoints } from "@ui/tokens/grid";
import { cva } from "class-variance-authority";

import { RenderWhenInView } from "../composers/RenderWhenInView";

export interface IAvatar {
  className?: string;
  height?: number;
  image?: IImage;
  link?: ILink;
  name?: string;
  width?: number;
  zIndex?: number;
}

interface AvatarProps extends VariantProps<typeof avatarVariants>, IAvatar {
  variant?: "default" | "minimal";
}

const avatarVariants = cva(
  [
    // default base classes
    "rounded-full overflow-hidden bg-default border border-overlay-darker",
  ],
  {
    variants: {
      withLink: {
        true: "group-hover:border-dark transition-all duration-300",
        false: "",
      },
      size: {
        xs: "w-20 h-20",
        sm: "w-28 h-28",
        md: "w-48 h-48",
        lg: "w-80 h-80",
        xl: "w-100 h-100",
        responsive: "w-48 h-48 md:w-80 md:h-80 xl:w-100 xl:h-100",
        unrestricted: "w-full",
      },
      variant: {
        default: "",
        minimal:
          "md:p-20 p-8 w-full flex items-center justify-center transition-transform duration-300 ease-in-out hover:scale-[0.98] bg-transparent border-none rounded-none",
      },
    },
    defaultVariants: {
      size: "responsive",
      variant: "default",
    },
  },
);

const Avatar = ({
  image,
  link,
  name,
  size = "responsive",
  className,
  height,
  width,
  zIndex,
  withLink,
  variant = "default",
}: AvatarProps) => {
  if (!isValidImage(image)) {
    return null;
  }

  const hasLink = !!link?.href;
  let sizes = "";

  if (!(variant === "minimal" && size === "unrestricted")) {
    switch (size) {
      case "xs":
        sizes = "20px";
        break;
      case "sm":
        sizes = "28px";
        break;
      case "md":
        sizes = "48px";
        break;
      case "lg":
        sizes = "80px";
        break;
      case "xl":
        sizes = "100px";
        break;
      case "responsive":
        sizes = `(min-width: ${breakpoints.xl}) 100px, (min-width: ${breakpoints.md}) 80px, 28px`;
        break;
    }
  }

  const AvatarImage = (
    <div className={cn(avatarVariants({ size, withLink: hasLink, variant, className }))} style={{ zIndex }}>
      <RenderWhenInView>
        <Image fit='contain' {...image} height={height} sizes={sizes || undefined} width={width} />
      </RenderWhenInView>
    </div>
  );

  return hasLink ? (
    <Link
      className={
        variant === "minimal"
          ? "block w-full transition-transform duration-300 ease-in-out hover:scale-95"
          : "effect-focus group relative overflow-hidden rounded-full"
      }
      {...link}
      label={undefined}
      title={link?.label || name}
    >
      {AvatarImage}
    </Link>
  ) : (
    AvatarImage
  );
};

Avatar.displayName = "Avatar";

interface AvatarListProps {
  avatars?: AvatarProps[];
  className?: string;
  title?: null | string;
}

const AvatarList = ({ avatars, className, title }: AvatarListProps) => {
  const _avatars = avatars?.filter(avatar => isValidImage(avatar.image)) || [];
  return (
    <div className={cn(className, "container grid-layout overflow-hidden pb-6")}>
      {title && <p className='f-ui-05 grid-col-span-12 mb-16 text-center md:mb-28'>{title}</p>}
      <div className='grid-col-span-12 flex justify-center lg:grid-col-span-6 lg:grid-col-start-4'>
        <div className='flex w-full flex-wrap justify-center'>
          {_avatars.map((avatar, index) => (
            <div className='flex w-1/4 items-center justify-center' key={index}>
              <Avatar className='flex-none' size='unrestricted' variant='minimal' {...avatar} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

AvatarList.displayName = "AvatarList";

export { Avatar, AvatarList };
